import React, { useEffect, useRef } from 'react';
import Color from 'color';
import { debounce, difference } from 'lodash';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import type { GroupedFieldVisibilities, StoreWithDistance } from './Locator';
import { LocatorResponse, TranslationTarget } from '../lib/dto';

export type MapFocus =
  | { type: 'point'; lat: number; lng: number; zoom?: number }
  | { type: 'store'; id: string };

export interface MapProps {
  stores: StoreWithDistance[];
  focus: MapFocus;
  markerColor: string;
  themeColor: string;
  translations: Partial<Record<TranslationTarget, { value: string }>>;
  groupedFieldVisibilities: GroupedFieldVisibilities;
  customFields: LocatorResponse['customFields'];
  showDetailsLabels: boolean;
  onMarkerClick: (store: StoreWithDistance) => void;
  onBoundsChanged: (bounds: google.maps.LatLngBounds) => void;
}

export const Map: React.FC<MapProps> = ({
  stores,
  focus,
  markerColor,
  themeColor,
  translations,
  groupedFieldVisibilities,
  customFields,
  showDetailsLabels,
  onMarkerClick,
  onBoundsChanged,
}) => {
  const map = useRef<google.maps.Map | null>(null);
  const markersByStoreId = useRef<
    Record<string, google.maps.marker.AdvancedMarkerElement>
  >({});
  const infoWindowsByStoreId = useRef<Record<string, google.maps.InfoWindow>>(
    {},
  );
  const markerClusterer = useRef<MarkerClusterer | null>(null);

  useEffect(() => {
    const updateMap = async () => {
      if (!map.current) {
        return;
      }

      // Clear markers
      const storeIdsForMarkersToDelete = difference(
        Object.keys(markersByStoreId.current),
        stores.map((s) => s.id),
      );
      storeIdsForMarkersToDelete.forEach((storeId) => {
        if (markersByStoreId.current[storeId]) {
          markersByStoreId.current[storeId].map = null;
          delete markersByStoreId.current[storeId];
          delete infoWindowsByStoreId.current[storeId];
        }
      });

      await google.maps.importLibrary('marker');

      // Add markers
      stores.forEach((store) => {
        let marker: google.maps.marker.AdvancedMarkerElement =
          markersByStoreId.current[store.id];

        if (marker) {
          return;
        }

        const pin = new google.maps.marker.PinElement({
          background: markerColor,
          borderColor: markerColor,
          glyphColor: Color(markerColor).isDark()
            ? Color(markerColor).lighten(0.3).hex()
            : Color(markerColor).darken(0.3).hex(),
        });
        marker = new google.maps.marker.AdvancedMarkerElement({
          map: map.current,
          position: { lat: store.lat, lng: store.lng },
          content: pin.element,
        });
        markersByStoreId.current[store.id] = marker;

        // Create container element
        let infoWindowContent = '<div style="color: #000000">';

        // Address
        infoWindowContent += `<div style="color: ${themeColor};"><strong>${store.name}</strong></div>`;
        infoWindowContent += `<div>${store.address1}</div>`;
        if (store.address2) {
          infoWindowContent += `<div>${store.address2}</div>`;
        }
        if (store.city || store.province || store.zip) {
          infoWindowContent += `<div>${[store.city, store.province, store.zip]
            .filter((component) => !!component)
            .join(', ')}</div>`;
        }
        if (store.country) {
          infoWindowContent += `<div>${store.country}</div>`;
        }

        // Search filters
        if (
          store.searchFilters.length > 0 &&
          groupedFieldVisibilities.targets.searchFilters.find(
            (v) => v.scope === 'map' && v.visible === true,
          )
        ) {
          const pieces = store.searchFilters.map((searchFilter) => {
            return `<div class="simple-store-locator-store-search-filter">${searchFilter.name}</div>`;
          });
          infoWindowContent += `<div style="margin-top: 10px;">${pieces.join(
            '',
          )}</div>`;
        }

        // Details
        const details = [];
        if (
          store.phone &&
          groupedFieldVisibilities.targets.phone.find(
            (v) => v.scope === 'map' && v.visible === true,
          )
        ) {
          details.push(
            `<div><a href="tel:${store.phone}" class="simple-store-locator-link" style="color: ${themeColor}">${store.phone}</a></div>`,
          );
        }
        if (
          store.email &&
          groupedFieldVisibilities.targets.email.find(
            (v) => v.scope === 'map' && v.visible === true,
          )
        ) {
          details.push(
            `<div><a href="mailto:${store.email}" target="_blank" class="simple-store-locator-link" style="color: ${themeColor}">${store.email}</a></div>`,
          );
        }
        if (
          store.website &&
          groupedFieldVisibilities.targets.website.find(
            (v) => v.scope === 'map' && v.visible === true,
          )
        ) {
          details.push(
            `<div><a href="${store.website}" target="_blank" class="simple-store-locator-link" style="color: ${themeColor}">${store.website}</a></div>`,
          );
        }
        if (
          store.notes &&
          groupedFieldVisibilities.targets.notes.find(
            (v) => v.scope === 'map' && v.visible === true,
          )
        ) {
          details.push(
            `<div class="simple-store-locator-multiline-value">${store.notes}</div>`,
          );
        }
        if (details.length > 0) {
          infoWindowContent += `<div style="margin-top: 10px;">${details.join(
            '',
          )}</div>`;
        }

        // Custom fields
        const customFieldsHtml = customFields
          .sort((a, b) => {
            return a.position - b.position;
          })
          .filter((customField) => {
            return !!groupedFieldVisibilities.customFields[
              customField.id
            ]?.find((v) => v.scope === 'map' && v.visible === true);
          })
          .map((customField) => {
            const value = store.customFieldValues.find(
              (fieldValue) => fieldValue.fieldId === customField.id,
            )?.value;

            if (!value) {
              return null;
            }

            if (customField.type === 'plain') {
              if (showDetailsLabels) {
                return `<div style="display: flex; gap: 5px;">
                  <span style="font-weight: 700;">${customField.name}:</span>
                  <span>${value}</span>
                </div>`;
              }

              return `<div>${value}</div>`;
            }

            if (customField.type === 'multiline') {
              if (showDetailsLabels) {
                return `<div>
                  <div><span style="font-weight: 700;">${customField.name}:</span></div>
                  <div class="simple-store-locator-multiline-value">${value}</div>
                </div>`;
              }
              return `<div>
                <div class="simple-store-locator-multiline-value">${value}</div>
              </div>`;
            }

            return '<div></div>';
          })
          .filter((v): v is string => v !== null);
        if (customFieldsHtml.length > 0) {
          infoWindowContent += `<div style="margin-top: 10px">${customFieldsHtml.join(
            '',
          )}</div>`;
        }

        // Get directions
        infoWindowContent += `<div style="margin-top: 10px;"><a href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
          [store.address1, store.city, store.province, store.zip, store.country]
            .filter((v) => !!v)
            .join(', '),
        )}&hl=${
          navigator.language
        }" target="_blank" class="simple-store-locator-link" style="color: ${themeColor}">${
          translations.getDirections?.value || 'Get directions'
        }</a></div>`;

        // Close container
        infoWindowContent += '</div>';

        const infoWindow = new google.maps.InfoWindow({
          content: infoWindowContent,
        });
        infoWindowsByStoreId.current[store.id] = infoWindow;

        marker.addListener('click', () => {
          // Close all info windows
          Object.keys(infoWindowsByStoreId.current).forEach((key) => {
            infoWindowsByStoreId.current[key].close();
          });

          infoWindow.open({
            map: map.current,
            anchor: marker,
          });

          onMarkerClick?.(store);
        });
      });

      // Enable marker clustering
      const markers = Object.values(markersByStoreId.current);

      if (markerClusterer.current) {
        // Marker clusterer exists already - clear existing markers and add new markers
        markerClusterer.current.clearMarkers();
        markerClusterer.current.addMarkers(markers);
      } else {
        // Marker cluster doesn't exist - initialize it
        markerClusterer.current = new MarkerClusterer({
          markers,
          map: map.current,
        });
      }
    };
    updateMap().catch((err) => {
      console.error(err);
    });
  }, [
    customFields,
    groupedFieldVisibilities.customFields,
    groupedFieldVisibilities.targets.email,
    groupedFieldVisibilities.targets.notes,
    groupedFieldVisibilities.targets.phone,
    groupedFieldVisibilities.targets.searchFilters,
    groupedFieldVisibilities.targets.website,
    markerColor,
    onMarkerClick,
    showDetailsLabels,
    stores,
    themeColor,
    translations.getDirections?.value,
  ]);

  useEffect(() => {
    if (!map.current) {
      return;
    }

    // Close all info windows
    Object.keys(infoWindowsByStoreId.current).forEach((key) => {
      infoWindowsByStoreId.current[key].close();
    });

    if (focus.type === 'store') {
      // Open info window for selected store
      const marker = markersByStoreId.current[focus.id];
      const infoWindow = infoWindowsByStoreId.current[focus.id];
      if (marker && infoWindow) {
        infoWindow.open({
          map: map.current,
          anchor: marker,
        });
      }
    } else {
      map.current.setCenter({ lat: focus.lat, lng: focus.lng });
      map.current.setZoom(focus.zoom || 10);
    }
  }, [focus]);

  const boundsChangedCallbackRef = useRef(
    debounce((bounds: google.maps.LatLngBounds) => {
      onBoundsChanged(bounds);
    }, 300),
  );

  return (
    <div
      ref={(ref) => {
        if (ref && !map.current) {
          map.current = new google.maps.Map(ref, {
            mapId: 'main',
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            controlSize: 25,
          });
          map.current.addListener('bounds_changed', () => {
            boundsChangedCallbackRef.current(
              map.current?.getBounds() || new google.maps.LatLngBounds(),
            );
          });
        }
      }}
      style={{ height: '100%' }}
    />
  );
};
